<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { ArticleDetail, ArticleSummaryRow } from '@/api/modules/articles'
import {
  createArticle,
  deleteArticle,
  fetchArticle,
  fetchArticles,
  patchArticle,
} from '@/api/modules/articles'
import { fetchPublicCategories, type CategoryRow } from '@/api/modules/portalPublic'
import { formatEpoch } from '@/utils/format'

const loading = ref(false)
const rows = ref<ArticleSummaryRow[]>([])
const categories = ref<CategoryRow[]>([])

const dlg = ref(false)
const dlgMode = ref<'add' | 'edit'>('add')
const editId = ref<number | null>(null)
const saving = ref(false)
const dlgLoading = ref(false)

const form = reactive({
  title: '',
  cid: 0,
  sort: 99,
  cont: '',
  keywords: '',
  description: '',
  thumbnail: '',
})

function fmtEpoch(sec?: number | null) {
  return formatEpoch(sec, '—')
}

function catLabel(c: CategoryRow) {
  const n = c.name?.trim() || c.ename?.trim() || '(未命名)'
  return `${c.id} · ${n}`
}

async function loadCategories() {
  try {
    const { data } = await fetchPublicCategories()
    categories.value = Array.isArray(data) ? data : []
  } catch {
    categories.value = []
  }
}

async function load() {
  loading.value = true
  try {
    const { data } = await fetchArticles()
    rows.value = Array.isArray(data) ? data : []
  } finally {
    loading.value = false
  }
}

function openAdd() {
  dlgMode.value = 'add'
  editId.value = null
  form.title = ''
  form.cid = 0
  form.sort = 99
  form.cont = ''
  form.keywords = ''
  form.description = ''
  form.thumbnail = ''
  dlg.value = true
}

async function openEdit(row: ArticleSummaryRow) {
  dlgMode.value = 'edit'
  editId.value = row.id
  dlgLoading.value = true
  dlg.value = true
  try {
    const { data } = await fetchArticle(row.id)
    const d = data as ArticleDetail
    form.title = d.title ?? ''
    form.cid = d.cid ?? 0
    form.sort = d.sort ?? 99
    form.cont = d.cont ?? ''
    form.keywords = d.keywords ?? ''
    form.description = d.description ?? ''
    form.thumbnail = d.thumbnail ?? ''
  } finally {
    dlgLoading.value = false
  }
}

function trimOrUndef(s: string) {
  const t = s.trim()
  return t === '' ? undefined : t
}

async function submitDlg() {
  if (!form.title.trim()) {
    ElMessage.warning('请填写文章标题')
    return
  }
  saving.value = true
  try {
    if (dlgMode.value === 'add') {
      await createArticle({
        title: form.title.trim(),
        cid: Number(form.cid ?? 0),
        sort: form.sort,
        cont: form.cont,
        keywords: trimOrUndef(form.keywords),
        description: trimOrUndef(form.description),
        thumbnail: trimOrUndef(form.thumbnail),
      })
      ElMessage.success('已添加')
    } else if (editId.value != null) {
      await patchArticle(editId.value, {
        title: form.title.trim(),
        cid: Number(form.cid ?? 0),
        sort: form.sort,
        cont: form.cont,
        keywords: trimOrUndef(form.keywords) ?? null,
        description: trimOrUndef(form.description) ?? null,
        thumbnail: trimOrUndef(form.thumbnail) ?? null,
      })
      ElMessage.success('已保存')
    }
    dlg.value = false
    load()
  } catch {
    /* axios interceptor */
  } finally {
    saving.value = false
  }
}

async function onDelete(row: ArticleSummaryRow) {
  try {
    await ElMessageBox.confirm(
      `确定删除文章「${row.title}」（ID ${row.id}）？此操作不可撤销。`,
      '删除确认',
      { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' },
    )
    await deleteArticle(row.id)
    ElMessage.success('已删除')
    await load()
  } catch (e) {
    if (e !== 'cancel') {
      /* interceptor */
    }
  }
}

onMounted(async () => {
  await loadCategories()
  await load()
})
</script>

<template>
  <PageChrome title="文章管理" description="对应旧版 Admin/Article；列表按排序号升序、添加时间降序。分类 cid 对齐 cat.id。">
    <template #actions>
      <el-space wrap>
        <el-button type="primary" @click="openAdd">新增文章</el-button>
        <el-button :loading="loading" @click="load">刷新</el-button>
      </el-space>
    </template>

    <el-card shadow="hover" class="ep-table-scope adm-card">
      <el-table v-loading="loading" :data="rows" stripe border size="default">
        <el-table-column prop="id" label="ID" width="72" align="center" />
        <el-table-column prop="title" label="文章标题" min-width="260" show-overflow-tooltip />
        <el-table-column prop="sort" label="排序" width="88" align="center" />
        <el-table-column label="添加时间" width="178" align="center">
          <template #default="{ row }">{{ fmtEpoch(row.addtime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="168" align="center" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link @click="openEdit(row)">编辑</el-button>
            <el-button type="danger" link @click="onDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="adm-foot">共 {{ rows.length }} 条</div>
    </el-card>

    <el-dialog
      v-model="dlg"
      :title="dlgMode === 'add' ? '新增文章' : '编辑文章'"
      width="680px"
      destroy-on-close
    >
      <div v-loading="dlgLoading">
        <el-form label-width="96px">
          <el-form-item label="标题" required>
            <el-input v-model="form.title" maxlength="500" show-word-limit />
          </el-form-item>
          <el-form-item label="分类">
            <el-select v-model="form.cid" filterable clearable placeholder="0 表示未分类" style="width: 100%">
              <el-option :value="0" label="0 · 未分类" />
              <el-option
                v-for="c in categories"
                :key="c.id"
                :value="c.id"
                :label="catLabel(c)"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="排序">
            <el-input-number v-model="form.sort" :min="0" :max="999999" />
          </el-form-item>
          <el-form-item label="正文">
            <el-input v-model="form.cont" type="textarea" :rows="10" placeholder="HTML 或纯文本" />
          </el-form-item>
          <el-form-item label="关键词">
            <el-input v-model="form.keywords" maxlength="500" />
          </el-form-item>
          <el-form-item label="摘要">
            <el-input v-model="form.description" type="textarea" :rows="3" maxlength="2000" />
          </el-form-item>
          <el-form-item label="缩略图 URL">
            <el-input v-model="form.thumbnail" maxlength="500" placeholder="/upload/image/…" />
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <el-button @click="dlg = false">取消</el-button>
        <el-button type="primary" :loading="saving" :disabled="dlgLoading" @click="submitDlg">
          保存
        </el-button>
      </template>
    </el-dialog>
  </PageChrome>
</template>

<style scoped lang="scss">
.adm-card {
  border-radius: var(--ep-radius-lg) !important;
}
.adm-foot {
  margin-top: 14px;
  font-size: 13px;
  color: var(--ep-text-muted);
}
</style>
